import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Logo from "../components/Logo";
import DomainLogo from "../components/DomainLogo";
import { getAccountInfo, AccountInfo } from "../api/services";
import { useDomainConfig } from "../hooks/useDomainConfig";

const Home: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug?: string }>();
  const { config: domainConfig } = useDomainConfig();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(!!slug);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    getAccountInfo(slug)
      .then((response) => {
        const data: AccountInfo = response.data;
        setLogoUrl(data.logoUrl);
        setCompanyName(data.companyName);
      })
      .catch((err) => {
        console.error(err);
        setError(t("home.errorLoading"));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [slug, t]);

  const handleGetStarted = () => {
    navigate("/login", { state: { logoUrl, companyName } });
  };

  return (
    <div className="mx-auto max-w-3xl py-10 text-center leading-relaxed">
      {loading ? (
        <p>{t("home.loading")}</p>
      ) : error ? (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">{t("home.errorLoading")}</div>
      ) : (
        <>
          {logoUrl ? (
            <Logo src={logoUrl} height={200} />
          ) : (
            <DomainLogo
              type="content"
              className="mx-auto block"
              style={{ maxHeight: 200, maxWidth: 300, marginBottom: "2rem" }}
              alt={t("logoAlt")}
              fallbackSrc="https://mylighthouse.co/assets/images/lighthouse.png"
            />
          )}

          <h1 className="mb-6">{t("home.welcome")}</h1>

          <p className="mb-4">{t("home.copy1")}</p>
          <p className="mb-6">
            {t("home.copy2Prefix")}
            <strong>
              <em>{t("home.copy2Emphasis")}</em>
            </strong>
            {t("home.copy2Suffix")}
          </p>
          <p className="mb-6">{t("home.copy3")}</p>

          <button
            className="rounded-lg bg-accent px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-accent-hover"
            onClick={handleGetStarted}
          >
            {t("home.button")}
          </button>
        </>
      )}
    </div>
  );
};

export default Home;
